import * as s from "styles/ModalCommonStyled";
import StepperComponent, { StepType } from "components/blocks/stepper/Stepper";
import BigButton from "components/atoms/buttons/BigButton";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/input/TextArea";
import { UPDATE_TYPE } from "./AddHealthContainer";
import { SearchRecorder } from "components/element/search/SearchRecorder";
import { HealthReport } from "api/interfaces/reportInterface.interface";
import AddNotification, { NOTIFICATION_TYPE } from "../common/AddNotification";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete";
import { Text, ValidateWord } from "components/atoms/text/Text";
import { ReportDataRow } from "api/interfaces/reportInterface.interface";
import Toggle from "components/atoms/toggle/Toggle";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useMemo } from "react";
import { useAuth } from "components/Auth";

type Props = {
  steps: Array<StepType>;
  page: number;
  selectedAccount: ProfileAccountInfo;
  onChangePage: (name: string, index?: number) => void;
  onChangeHealthReportInfo: (
    type: UPDATE_TYPE,
    value: string | number | boolean | string[] | boolean[] | number[]
  ) => void;
  onChangeNotification: (
    type: NOTIFICATION_TYPE,
    value: string | number | boolean | string[] | boolean[] | number[]
  ) => void;
  hostName: string;
  onClickCreate: () => void;
  data: HealthReport | HealthReport;
  // onClickDelete: () => void;
  isEdit: boolean | undefined;
  isConfirm: boolean;
  setIsDeleteConfirm?: (confirm: boolean) => void;
  onDelete: (value: boolean) => void;
  onConfirmDelete: () => void;
  onChangeEnable: (value: boolean) => void;
  reportRow?: ReportDataRow;
  reportRuleValidate: Validate;
  reportRuleValidateMsg: ValidateMsg;
  saveIsInProgress: boolean;
  deleteIsInProgress: boolean;
};

export default function AddHealthPresenter(props: Props): JSX.Element {
  const auth = useAuth();

  const isDefault = props.reportRow?.isDefault;
  const isEditable = useMemo(() => {
    if (isDefault) {
      return props.selectedAccount.isAdminGroup;
    }
    if (props.isEdit) {
      return (
        auth.user.email === props.reportRow?.email ||
        props.selectedAccount.isAdminGroup
      );
    }
    return true;
  }, [
    auth.user.email,
    isDefault,
    props.isEdit,
    props.reportRow?.email,
    props.selectedAccount.isAdminGroup,
  ]);

  return (
    <>
      <StepperComponent
        activeStep={props.page}
        steps={props.steps}
        onClick={props.onChangePage}
        data-testid="reports-add-health-stepper-component-change-page"
      />

      <s.ModalInnerHalf>
        <div style={{ display: props.page === 0 ? "block" : "none" }}>
          <s.CheckInput>
            <input
              type="checkbox"
              // defaultChecked={props.data.recorder.autoApply}
              checked={props.data.recorder.autoApply}
              onChange={(e) => {
                props.onChangeHealthReportInfo(
                  UPDATE_TYPE.AUTO_APPLY,
                  e.currentTarget.checked
                );
              }}
              disabled={isDefault}
              data-testid="reports-add-health-input-auto-apply-recorder"
            />{" "}
            Apply to all current and future recorders.
          </s.CheckInput>

          {!props.data.recorder.autoApply && (
            <s.ModalTableContainer>
              <SearchRecorder
                isTab={true}
                selectedRow={props.data.recorder.systems}
                selectedGroupRow={props.data.recorder.recorderGroupId}
                accountId={props.selectedAccount.accountId}
                onSelectedRowsChange={(value: string[]) =>
                  props.onChangeHealthReportInfo(UPDATE_TYPE.RECORDER, value)
                }
                onSelectedGroupRowsChange={(value: number[]) =>
                  props.onChangeHealthReportInfo(
                    UPDATE_TYPE.RECORDER_GROUP,
                    value
                  )
                }
                userId={
                  props.isEdit ? props.data.preferences?.userId : undefined
                }
                isDisabled={isDefault}
              />
            </s.ModalTableContainer>
          )}

          <ValidateWord data-testid="reports-add-health-validate-recorder">
            {props.reportRuleValidateMsg.isRecorder}
          </ValidateWord>
        </div>
        <div style={{ display: props.page === 1 ? "block" : "none" }}>
          <AddNotification
            data={props.data.notification}
            accountId={props.selectedAccount.accountId}
            onChange={props.onChangeNotification}
            isEndOfMonth={props.data.notification.isEndOfMonth}
          />
        </div>
        <div style={{ display: props.page === 2 ? "block" : "none" }}>
          <ModalInput label="Title" essential>
            <Input
              placeholder="Enter a title for this report"
              value={props.data.preferences.title}
              onChange={(e) =>
                props.onChangeHealthReportInfo(
                  UPDATE_TYPE.REPORT_NAME,
                  e.target.value
                )
              }
              disabled={isDefault}
              maxLength={255}
              data-testid="reports-add-health-input-change-health-report-info-2"
            />

            <ValidateWord data-testid="reports-add-health-validate-rule-name">
              {props.reportRuleValidateMsg.isRuleName}
            </ValidateWord>
          </ModalInput>
          <ModalInput label="Host">
            <Input disabled value={props.data.preferences.host} />
          </ModalInput>
          <ModalInput label="Description">
            <TextArea
              placeholder="Please enter a description"
              value={props.data.preferences.description}
              onChange={(e) =>
                props.onChangeHealthReportInfo(UPDATE_TYPE.DESC, e.target.value)
              }
              disabled={isDefault}
              maxLength={255}
              data-testid="reports-add-health-textarea-change-health-report-info"
            />
          </ModalInput>
          {props.isEdit && (
            <s.RowModalInput>
              <Text bold>Enable</Text>
              <Toggle
                isToggled={
                  props.isEdit
                    ? (props.data.preferences?.isEnable as boolean)
                    : true
                }
                checked={
                  props.isEdit
                    ? (props.data.preferences?.isEnable as boolean)
                    : true
                }
                onChange={(
                  checked: boolean,
                  event: React.MouseEvent<HTMLButtonElement>
                ) => {
                  // e.stopPropagation();
                  props.onChangeEnable(checked);
                }}
                dataTestId="reports-add-health-enable-toggle"
              />
            </s.RowModalInput>
          )}
        </div>
      </s.ModalInnerHalf>
      <s.ButtonWrapper>
        {props.isEdit && !isDefault && isEditable && (
          <BigButton
            buttonType="warning"
            label="Delete"
            onClickBtn={() =>
              props.setIsDeleteConfirm !== undefined
                ? props.setIsDeleteConfirm(true)
                : null
            }
            disabled={props.deleteIsInProgress}
            data-testid="reports-add-health-button-set-is-delete-confirm"
          />
        )}

        {props.page !== 0 && (
          <BigButton
            buttonType="cancel"
            label="Previous"
            onClickBtn={() => props.onChangePage("previous")}
            data-testid="reports-add-health-button-change-page"
          />
        )}

        {props.page === 2 ? (
          (props.isEdit && isEditable) || !props.isEdit ? (
            <BigButton
              label="Save"
              onClickBtn={props.onClickCreate}
              disabled={props.saveIsInProgress}
              data-testid="reports-add-health-button-click-create"
            />
          ) : (
            <></>
          )
        ) : (
          <BigButton
            buttonType="cancel"
            label="Next"
            onClickBtn={() => props.onChangePage("next")}
            data-testid="reports-add-health-button-change-page-2"
          />
        )}

        {props.isConfirm && (
          <DeleteConfirmModal
            label="report rule"
            onDelete={() => props.onDelete(false)}
            onConfirmDelete={props.onConfirmDelete}
          />
        )}
      </s.ButtonWrapper>
    </>
  );
}
